
import { motion } from "framer-motion";
import { Calendar, Clock } from "lucide-react";
import { BlogPost } from "@/hooks/useBlogPosts";
import "../blog-management/RichTextEditor.css"; // Import shared styles
import { UnsplashAttribution } from "@/components/blog-management/UnsplashImage";

interface BlogContentProps {
  post: BlogPost;
}

export const BlogContent = ({ post }: BlogContentProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const estimateReadingTime = (content: string = "") => {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  // Check if image is from Unsplash by looking at metadata
  const isUnsplashImage = post.metadata?.unsplash_image && 
    post.metadata.unsplash_attribution &&
    post.metadata.unsplash_attribution.photographerName;

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="prose prose-slate max-w-none"
    >
      {post.image_url && (
        <div className="relative aspect-[2/1] mb-8 overflow-hidden rounded-lg">
          <img
            src={post.image_url}
            alt={post.title}
            className="object-cover w-full h-full"
          />
          
          {/* Show Unsplash attribution if applicable */}
          {isUnsplashImage && (
            <div className="absolute bottom-0 right-0 bg-black/50 text-white p-1 px-2 rounded-tl-md">
              <UnsplashAttribution
                photographerName={post.metadata.unsplash_attribution.photographerName}
                photographerUsername={post.metadata.unsplash_attribution.photographerUsername}
                photographerUrl={post.metadata.unsplash_attribution.photographerUrl}
                unsplashUrl={post.metadata.unsplash_attribution.unsplashUrl}
                className="text-white/90 text-[10px]"
              />
            </div>
          )}
        </div>
      )}

      <header className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">{post.title}</h1>
        <div className="flex items-center gap-6 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <time dateTime={post.created_at}>
              {formatDate(post.created_at)}
            </time>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{estimateReadingTime(post.content)} min read</span>
          </div>
        </div>
      </header>

      {post.excerpt && (
        <p className="text-xl text-slate-600 mb-8 leading-relaxed">
          {post.excerpt}
        </p>
      )}

      <div className="prose prose-slate prose-lg max-w-none ProseMirror">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p className="text-slate-600">No content available.</p>
        )}
      </div>
    </motion.article>
  );
};
